import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { ServiceCategoryEnum } from "../../types/service.type";

import { GO_SERVICE_QUERY_KEYS } from "../queryKeys";
import { getOrderedRequests } from "../services/goHireService";

const REQUESTS_PAGE_LIMIT = 3;

interface UseOrderedRequestsParams {
  limit?: number;
  offset?: number;
  category?: ServiceCategoryEnum;
  sort?: Record<string, "ASC" | "DESC">;
  enabled?: boolean;
}

interface UseOrderedRequestsPaginatedParams extends UseOrderedRequestsParams {
  page?: number;
}

export const useOrderedRequestsPaginated = ({
  limit = REQUESTS_PAGE_LIMIT,
  page = 0,
  category,
  sort,
  enabled,
}: UseOrderedRequestsPaginatedParams = {}) => {
  return useQuery({
    queryKey: [...GO_SERVICE_QUERY_KEYS.orderedRequests(), { limit, page, category, sort }],
    queryFn: () => getOrderedRequests({ limit, offset: page * limit, category, sort }),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!category && enabled,
  });
};

export const useOrderedRequestsInfinite = ({
  limit = REQUESTS_PAGE_LIMIT,
  category,
  sort,
  enabled,
}: Omit<UseOrderedRequestsParams, "offset"> = {}) => {
  return useInfiniteQuery({
    queryKey: [...GO_SERVICE_QUERY_KEYS.orderedRequests(), { limit, category, sort }],
    queryFn: ({ pageParam = 0 }) => getOrderedRequests({ limit, offset: pageParam * limit, category, sort }),
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.length * limit;
      return fetchedCount < lastPage.data.meta.totalItems ? allPages.length : undefined;
    },
    initialPageParam: 0,
    enabled: !!category && enabled,
  });
};
